import React from 'react';
import Dialog from 'Components/Core/Dialog';
import './ShareTargetDialog.css';

// Empfaengt Bilder/Dateien, die auf Android ueber den System-"Teilen"-Dialog
// einer anderen App (z.B. Fotos) an diese installierte PWA geschickt wurden
// (Web Share Target API, siehe manifest.json "share_target" + Backend/share.php).
// share.php speichert die Datei serverseitig und leitet mit "?shared=1" auf die
// App zurueck - hier wird das erkannt, der Inhalt einmalig per GetPendingShare
// abgeholt und der Nutzer gefragt, in welches Forum/an welches Mitglied der
// Inhalt eingefuegt werden soll (dort dann als vorausgefuellter "Neuer
// Beitrag"-Dialog, damit vor dem Senden noch etwas ergaenzt werden kann).
export default function ShareTargetDialog(props) {

	const x = props.x;
	const [share, setShare] = React.useState(null);
	const [query, setQuery] = React.useState('');

	x.wad.init(props.id);

	// Eigener Mount-Effekt statt eines von aussen (Content.js) getimten Aufrufs -
	// ein setTimeout(0) direkt nach addDialog() traf dieses Dialog teils an, bevor
	// es tatsaechlich gerendert/gemountet war (beobachtet 2026-08-04: x.was.check-
	// PendingShare war zu dem Zeitpunkt noch keine Funktion). useEffect garantiert,
	// dass load() erst NACH dem eigenen Mount laeuft.
	React.useEffect(() => {
		load();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function load() {
		if (window.location.search.indexOf('shared=1') === -1) return;
		// Sofort aus der URL entfernen, damit ein spaeterer Reload nicht denselben
		// Inhalt erneut anbietet (GetPendingShare raeumt serverseitig zwar auch aus
		// der Session auf, ein Reload VOR dieser Antwort wuerde die Anfrage aber
		// doppelt auslösen).
		const url = new URL(window.location.href);
		url.searchParams.delete('shared');
		window.history.replaceState({}, '', url.pathname + url.search + url.hash);

		x.was.axGet('Backend/webapp.php?module=System&task=GetPendingShare', (result) => {
			if (result.data.returncode === 1) {
				const content = result.data.content;
				if (content.error) {
					x.wah.addHint(content.error, true);
					return;
				}
				if (!content.fileName && !content.text && !content.title) return;
				setShare(content);
				setQuery('');
				x.wad[props.id].show();
			}
		});
	}

	function matches(name) {
		return query.trim() === '' || name.toLowerCase().includes(query.trim().toLowerCase());
	}

	function makeComposerContent() {
		let parts = [];
		if (share.fileName) {
			const url = 'Backend/download.php?file=' + share.fileName + '&name=' + encodeURIComponent(share.displayName);
			parts.push('[url=' + url + ']' + share.displayName + '[/url]');
		}
		if (share.text) parts.push(share.text);
		else if (share.title) parts.push(share.title);
		return parts.join(String.fromCharCode(10));
	}

	// Neu erzeugte Foren/private Unterhaltungen werden asynchron gemountet
	// (x.was.addModule() -> React-Render) - showDialogNewThread steht daher nicht
	// zwingend schon im selben Tick zur Verfuegung. Kurz mit Zeitlimit abwarten,
	// statt eine feste Verzoegerung zu raten.
	function waitForForum(forumId, attempt = 0) {
		if (typeof x.wam[forumId] !== 'undefined' && typeof x.wam[forumId].showDialogNewThread === 'function') {
			x.wam[forumId].showDialogNewThread(makeComposerContent());
			x.wad[props.id].hide();
			return;
		}
		if (attempt >= 50) {
			x.wah.addHint('Forum konnte nicht geöffnet werden.', true);
			x.wad[props.id].hide();
			return;
		}
		setTimeout(() => waitForForum(forumId, attempt + 1), 100);
	}

	function selectForum(forum) {
		x.wam.openForum(String(forum.id), forum.name);
		waitForForum(String(forum.id));
	}

	function selectMember(member) {
		x.wam.openPrivateForum(member.memberId, member.name, member.level, true);
		waitForForum(x.wau.getPrivateForumId(member.memberId));
	}

	if (share === null) {
		return <Dialog id={props.id} x={x} display="d-none" classes="dialog-share-target" title="Teilen" modal={true} />;
	}

	const filteredForums = [{ id: 'forum', name: x.was.application }, ...Object.values(x.was.foren || {})]
		.filter((forum) => parseInt(forum.disabled, 10) !== 1)
		.filter((forum) => matches(forum.name));

	const filteredMembers = Object.keys(x.was.members || {})
		.map((memberId) => ({ memberId, ...x.was.members[memberId] }))
		.filter((member) => parseInt(member.memberId, 10) !== parseInt(x.was.memberId, 10))
		.filter((member) => parseInt(member.locked, 10) !== 1)
		.filter((member) => matches(member.name));

	return (
		<Dialog id={props.id} x={x} display="d-none" classes="dialog-share-target" title="Teilen" modal={true}>
			<div className="share-target-preview">
				{share.displayName || share.title || share.text}
			</div>
			<input
				type="text"
				className="share-target-filter"
				placeholder="Filtern…"
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				autoFocus
			/>
			<div className="share-target-results">
				{filteredForums.length > 0 &&
					<div className="share-target-group-label">Foren</div>
				}
				{filteredForums.map((forum) =>
					<div key={'forum-' + forum.id} className="share-target-result" onClick={() => selectForum(forum)}>
						<i className="bi bi-chat-square-text share-target-result-icon" />
						<span className="share-target-result-label">{forum.name}</span>
					</div>
				)}
				{filteredMembers.length > 0 &&
					<div className="share-target-group-label">Mitglieder</div>
				}
				{filteredMembers.map((member) =>
					<div key={'member-' + member.memberId} className="share-target-result" onClick={() => selectMember(member)}>
						<i className="bi bi-person share-target-result-icon" />
						<span className="share-target-result-label">{member.name}</span>
					</div>
				)}
				{filteredForums.length === 0 && filteredMembers.length === 0 &&
					<div className="share-target-result share-target-no-result">Keine Treffer</div>
				}
			</div>
		</Dialog>
	)

};
